/**
 * @typedef {Object} PortalV2PromotionApiItem
 * @property {number} id
 * @property {string} type
 * @property {string} [slug]
 * @property {string} promotion_start
 * @property {string} promotion_end
 * @property {Record<string, string>} name
 * @property {Record<string, string>} description
 * @property {Record<string, string>} [full_text]
 * @property {Record<string, string>} [long_description]
 * @property {Record<string, string[]>} [rules]
 * @property {string|null} image
 * @property {string|null} image_url
 * @property {number} promotion_value
 * @property {'multiply'|'add'} amount_type
 * @property {boolean} [signed_in]
 * @property {string} [visualType]
 * @property {boolean} [featured]
 * @property {boolean} [is_active]
 * @property {string} [status]
 * @property {Array<{labelKey?: string, label?: Record<string, string>, valueKey?: string, value?: Record<string, string>}>} [highlights]
 */

const FALLBACK_VISUAL_TYPES = [
  "rain_tire",
  "road_car",
  "trucks_road",
  "truck_side",
  "logistics_map",
  "motorcycle",
  "wet_wheel",
];

export function pickLocalizedField(value, locale) {
  if (value == null) return "";

  if (typeof value === "string") return value.trim();

  if (typeof value === "object" && !Array.isArray(value)) {
    const tryKeys = [
      locale,
      locale?.replace("_", "-"),
      locale?.replace("-", "_"),
    ].filter(Boolean);

    for (const key of tryKeys) {
      const candidate = value[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }

    const languagePart = locale?.split("-")[0];
    if (languagePart) {
      const match = Object.keys(value).find(
        (key) => key?.split("-")[0] === languagePart,
      );
      if (match && value[match]) {
        return String(value[match]).trim();
      }
    }

    const first = Object.values(value).find(
      (entry) => typeof entry === "string" && entry.trim(),
    );
    return first ? String(first).trim() : "";
  }

  return String(value ?? "");
}

/**
 * Tries each source until a non-empty localized string is found.
 * Needed when API returns empty `full_text` objects while `description` has content.
 *
 * @param {Array<unknown>} sources
 * @param {string} locale
 * @returns {string}
 */
export function pickLocalizedFieldFromSources(sources, locale) {
  for (const source of sources) {
    if (source == null) {
      continue;
    }

    const picked = pickLocalizedField(source, locale);
    if (picked) {
      return picked;
    }
  }

  return "";
}

export function pickLocalizedList(value, locale) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  if (typeof value === "object") {
    const localized = value[locale] ?? value[locale?.replace("_", "-")];
    if (Array.isArray(localized)) {
      return localized.map((entry) => String(entry).trim()).filter(Boolean);
    }
  }

  return [];
}

const ENDING_SOON_MS = 7 * 24 * 60 * 60 * 1000;

export function derivePromotionStatus(item) {
  if (item.status) {
    return item.status;
  }

  const now = Date.now();
  const startMs = item.promotion_start
    ? new Date(item.promotion_start).getTime()
    : NaN;
  const endMs = item.promotion_end
    ? new Date(item.promotion_end).getTime()
    : NaN;

  if (!Number.isNaN(startMs) && now < startMs) {
    return "planned";
  }

  if (!Number.isNaN(endMs)) {
    if (now > endMs) {
      return "ended";
    }
    if (endMs - now <= ENDING_SOON_MS) {
      return "ending_soon";
    }
  }

  return item.is_active === false ? "planned" : "active";
}

export function slugifyPromotionText(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/**
 * @param {PortalV2PromotionApiItem} item
 * @param {string} locale
 * @param {number} index
 */
export function toPortalPromotionSlug(item, locale, index = 0) {
  if (item.slug) {
    return String(item.slug).trim();
  }

  const fromName = slugifyPromotionText(pickLocalizedField(item.name, locale));
  return fromName || `promotion-${item.id ?? index + 1}`;
}

function mapHighlights(item, locale) {
  if (!Array.isArray(item.highlights)) {
    return [];
  }

  return item.highlights.map((entry) => ({
    label: entry.labelKey
      ? null
      : pickLocalizedField(entry.label, locale),
    labelKey: entry.labelKey ?? null,
    value: entry.valueKey ? null : pickLocalizedField(entry.value, locale),
    valueKey: entry.valueKey ?? null,
  }));
}

/**
 * @param {PortalV2PromotionApiItem} item
 * @param {string} locale
 * @param {number} index
 */
export function toPortalPromotionCardData(item, locale, index = 0) {
  const visualType =
    item.visualType || FALLBACK_VISUAL_TYPES[index % FALLBACK_VISUAL_TYPES.length];
  const slug = toPortalPromotionSlug(item, locale, index);
  const description = pickLocalizedFieldFromSources(
    [item.description, item.full_text],
    locale,
  );
  const longDescription = pickLocalizedFieldFromSources(
    [item.full_text, item.long_description, item.description],
    locale,
  );
  const status = derivePromotionStatus(item);

  return {
    id: item.id,
    slug,
    type: visualType,
    imageSrc: item.image_url || item.image || null,
    title: pickLocalizedField(item.name, locale),
    description: description || longDescription,
    longDescription: longDescription || description,
    rules: pickLocalizedList(item.rules, locale),
    highlights: mapHighlights(item, locale),
    amountType: item.amount_type,
    promotionValue: item.promotion_value,
    signedIn: Boolean(item.signed_in),
    requiresSignIn: item.type === "sign_in_required",
    promotionStart: item.promotion_start,
    promotionEnd: item.promotion_end,
    featured: Boolean(item.featured),
    isActive: status !== "ended" && status !== "planned" && item.is_active !== false,
    status,
  };
}

/**
 * @param {PortalV2PromotionApiItem[]} items
 * @param {string} locale
 */
export function mapPortalPromotionsFromApi(items, locale) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item, index) => toPortalPromotionCardData(item, locale, index));
}

export function filterPortalPromotions(items, filterId = "all") {
  if (!Array.isArray(items)) {
    return [];
  }

  switch (filterId) {
    case "active":
      return items.filter(
        (item) =>
          item.isActive !== false &&
          item.status !== "ended" &&
          item.status !== "planned",
      );
    case "sign_in_required":
      return items.filter((item) => item.requiresSignIn && !item.signedIn);
    case "signed":
      return items.filter((item) => item.requiresSignIn && item.signedIn);
    default:
      return items;
  }
}
